import { makeObservable, action, observable, computed } from 'mobx';
import axios from 'axios';
import { RootStore } from 'stores';

import {
  IAuth,
  IchangePassApiData,
  ILoginApiData,
  IVerifyOtpApiData
} from 'types';
import { getAuth, setAuth } from 'services/localStorage';
import { allowedUserType, MESSAGES, toast, toastTypeEnum } from 'common/utils';
import { getApiErrorMessage } from 'services/utils';
import {
  changePassService,
  loginService,
  verifyOtpService,
  sendEmailWithCreds
} from 'services/users';
import { AxiosError } from 'axios';
import { getFullUrl, SOURCE_STR } from 'services';

export type IInitialState = {
  data: IAuth;
  ui: {
    changePassModal: boolean;
    changePassLoading: boolean;
    sendEmailWithCredsLoading: boolean;
    verifyingToken: boolean;
    showTwoFA: string | null;
  };
};

export const initialState: IInitialState = {
  data: {
    expires: null,
    token: null,
    user: null
  },
  ui: {
    changePassLoading: false,
    sendEmailWithCredsLoading: false,
    changePassModal: false,
    verifyingToken: false,
    showTwoFA: null
  }
};

export class AuthStore {
  state: IInitialState = initialState;
  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    makeObservable(this, {
      state: observable,
      onLogin: action,
      verifyUserToken: action,
      verifyOtp: action,
      onLogOut: action,
      changePass: action,
      sendEmailWithCreds: action,
      setChangePassModal: action,
      isAdmin: computed,
      isEmployee: computed,
      isDistributor: computed,
      setState: action,
      reset: action
    });

    this.rootStore = rootStore;
    /* use this space to hydrate state from localStorage */
    const auth_data = getAuth();

    if (auth_data?.token) this.verifyUserToken(auth_data?.token);
    // const state_data: IAuth = auth_data?.token ? auth_data : initialState.data;
    // const state: IInitialState = {
    //   ...initialState,
    //   data: state_data
    // };
    // this.setState(state);
    // setAuth({ value: state_data });
  }

  onLogin = async (params: ILoginApiData) => {
    try {
      const { data: loginResData } = await loginService(params);
      const authData: IAuth = {
        token: loginResData.idToken,
        user: loginResData.user,
        expires: loginResData.expiresIn
      };
      if (!allowedUserType(loginResData.user)) {
        return toast(toastTypeEnum.error, 'Not enough permission to login');
      }
      setAuth({
        value: authData
      });
      this.setState({
        data: authData
      });
    } catch (error) {
      if (error) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.status === 406) {
          this.state.ui.showTwoFA = params.email;
        }
      }
      const message = getApiErrorMessage(error);
      message && toast(toastTypeEnum.error, message);
    } finally {
    }
  };

  verifyUserToken = async (token: string | null) => {
    try {
      const authData = getAuth()
      this.setState({ ui: { ...this.state.ui, verifyingToken: true } });
      await axios.post(
        getFullUrl("/auth/validate-token"),
        null,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            source: SOURCE_STR
          }
        })
      this.setState({ data: authData })
    } catch (error) {
      const message = getApiErrorMessage(error);
      message && toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, verifyingToken: false } });
    }
  };

  verifyOtp = async (params: IVerifyOtpApiData) => {
    try {
      const { data: loginResData } = await verifyOtpService(params);
      if (!allowedUserType(loginResData.user)) {
        return toast(toastTypeEnum.error, 'Not enough permission to login');
      }
      const authData: IAuth = {
        token: loginResData.idToken,
        user: loginResData.user,
        expires: loginResData.expiresIn
      };
      setAuth({
        value: authData
      });
      this.setState({
        data: authData
      });
    } catch (error) {
      const message = getApiErrorMessage(error);
      message && toast(toastTypeEnum.error, message);
    }
  };

  onLogOut = () => {
    this.reset();
    this.rootStore.UsersStore.reset();
  };

  changePass = async (apiData: IchangePassApiData) => {
    try {
      this.state.ui.changePassLoading = true;
      await changePassService(apiData);
      toast(toastTypeEnum.success, MESSAGES.passChangeSuccess);
      this.state.ui.changePassModal = false;
      return true;
    } catch (error) {
      const message = getApiErrorMessage(error);
      message && toast(toastTypeEnum.error, message);
      return false;
    } finally {
      this.state.ui.changePassLoading = false;
    }
  };

  sendEmailWithCreds = async (id: string) => {
    try {
      this.state.ui.sendEmailWithCredsLoading = true;
      await sendEmailWithCreds(id);
      toast(toastTypeEnum.success, MESSAGES.sendMailSuccess);
      this.state.ui.changePassModal = false;
      return true;
    } catch (error) {
      const message = getApiErrorMessage(error);
      message && toast(toastTypeEnum.error, message);
      return false;
    } finally {
      this.state.ui.sendEmailWithCredsLoading = false;
    }
  };

  setChangePassModal = (value: boolean) => {
    this.state.ui.changePassModal = value;
  };

  get isAdmin() {
    return (
      this.state.data.user?.userType === 'admin' ||
      this.state.data.user?.userType === 'super admin'
    );
  }

  get isEmployee() {
    return this.state.data.user?.userType === 'employee';
  }

  get isDistributor() {
    return this.state.data.user?.userType === 'distributor';
  }

  setState = (params: Partial<IInitialState>) => {
    const { state } = this;
    this.state = {
      ...state,
      ...params
    };
    /* use this space to save/sync state with localStorage */
    setAuth({ value: this.state.data }); // keeping localStorage in sync with store
  };

  reset = () => {
    this.state = initialState;
    /* use this space to reset localStorage in case if you're persisting this state */
    setAuth({ value: initialState.data });
  };
}
